import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, take, takeUntil, timeout } from 'rxjs/operators';

import { CommandService } from '@core/services/command.service';
import { PlatformCommand } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { AuthorizationType } from '@shared/models/AuthorizationType';
import { getUserInfo, isAuthenticated } from '@shared/store/selectors/security.selector';

import { markSqaRunSignInRecorded, sqaRunSignInToRecord } from './sqa-run-mode';

// RecordSqaRunSignInCommandConsumer (XatBotApi, TSP-E-157-26): the run key goes; WHO signed in is read off the token
// on the server, and the server's Teamspace credential writes it — the browser never holds one.
export const SQA_RUN_SIGN_IN_COMMAND = 'RecordSqaRunSignInCommand';
const SQA_RUN_KEY_FIELD = 'RunKey';
// The answer comes back over the socket or, failing that, the platform's HTTP poll (first at ~15 s, then every 10 s):
// the bound leaves room for three polls, so a slow answer is still read rather than mistaken for none.
const RECORD_TIMEOUT_MS = 45000;

// What a refusal says, read off whichever field the platform put the server's exception in (a Failed response
// carries it as SnackBarMessage) — so the console names the reason, never just "TimeoutError" or "[object Object]".
export function sqaSignInRefusalOf(response: unknown): string {
  const values = (response as { fieldValues?: Record<string, unknown> })?.fieldValues ?? {};
  const said = values['SnackBarMessage'] ?? values['Error'] ?? values['ErrorMessage'];
  return typeof said === 'string' ? said.trim() : '';
}

/**
 * ITP RECORDS THE SIGN-IN ON THE RUN (TSP-E-157-26). Once an SQA session is signed in to a company, the run moves
 * `ready → running` and its page shows who it signed in as — what the local IdP used to write before the IdP stopped
 * knowing about SQA. Once per run per tab: a recorded key is not sent again. A refusal is logged, never blocking —
 * the Playground still runs the suite, and the run page only misses the line. A refusal is logged BY NAME
 * (TSP-E-157-27): the server's reason when it gave one, else that the API did not answer in time.
 */
@Injectable({ providedIn: 'root' })
export class SqaRunSignInService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private started = false;

  constructor(private readonly securityStore: Store<any>, private readonly commandService: CommandService) {}

  start(): void {
    if (this.started || !sqaRunSignInToRecord()) return;
    this.started = true;

    combineLatest([this.securityStore.select(isAuthenticated), this.securityStore.select(getUserInfo)])
      .pipe(
        filter(([auth, userInfo]) => auth === AuthorizationType.AUTHORIZED && !!userInfo?.companyId),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.record());
  }

  private record(): void {
    const runKey = sqaRunSignInToRecord();
    if (!runKey) return;
    // Taken before the answer, so a slow call is never sent twice; a refusal says why in the console.
    markSqaRunSignInRecorded(runKey);

    const command = new PlatformCommand(SQA_RUN_SIGN_IN_COMMAND, Api.XatBotApi);
    command.fieldValues[SQA_RUN_KEY_FIELD] = runKey;
    this.commandService
      .executePlatformCommand(command)
      .pipe(timeout({ first: RECORD_TIMEOUT_MS }), take(1), takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const refusal = sqaSignInRefusalOf(response);
          if (!response?.fieldValues?.['CompanyId'] && refusal) {
            console.warn(`[SQA] ${runKey}: the sign-in was not recorded on the run — ${refusal}`);
          }
        },
        error: error => console.warn(`[SQA] ${runKey}: the sign-in was not recorded on the run — ${
          (error as { name?: string })?.name === 'TimeoutError'
            ? `the API did not answer in ${RECORD_TIMEOUT_MS / 1000} s. Check that its XatBotApi consumers are running with EnableTestRunner=true.`
            : sqaSignInRefusalOf(error) || 'the request failed.'}`)
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
